/**
 * How a saved artifact is grouped, for UI presentation and for targeting when a
 * generation is assembled. One list, used by the Mongo enum (`models/Artifact.js`), the
 * api's validation (`routes/artifacts.ts`) and the frontend's scope dropdown, so a new
 * scope can't be added to one and missed by the others.
 *
 * @input ArtifactScope.Character  @output 'character'
 */
export const ArtifactScope = {
    /** Camera shot style (steadicam, hand held, static, ...) */
    Camera: 'camera',
    /** Lens type used for the shot (medium shot, long shot, ...) */
    Lens: 'lens',
    /** Lighting (spot, soft, dramatic, day time, ...) */
    Light: 'light',
    /** Visual style (3d, realistic, cartoon, ...) */
    Style: 'style',
    /** Describes a character, for reuse across that character's generations */
    Character: 'character',
    /** Describes an instruction, for reuse across different contexts */
    Instruction: 'instruction',
    /** Describes a location, for reuse across different contexts */
    Location: 'location',
    /** Describes a prop, for reuse across different contexts */
    Prop: 'prop',
    /** A general artifact the user saved to recall later */
    User: 'user',
} as const

export type ArtifactScope = (typeof ArtifactScope)[keyof typeof ArtifactScope]

export const ARTIFACT_SCOPES: ArtifactScope[] = Object.values(ArtifactScope)

export const DEFAULT_ARTIFACT_SCOPE: ArtifactScope = ArtifactScope.User

/** Dropdown labels. Keyed by scope so a missing label is a compile error. */
export const ARTIFACT_SCOPE_LABELS: Record<ArtifactScope, string> = {
    [ArtifactScope.Camera]: 'Camera',
    [ArtifactScope.Lens]: 'Lens',
    [ArtifactScope.Light]: 'Lighting',
    [ArtifactScope.Style]: 'Style',
    [ArtifactScope.Character]: 'Character',
    [ArtifactScope.Location]: 'Location',
    [ArtifactScope.Prop]: 'Prop',
    [ArtifactScope.Instruction]: 'Instruction',
    [ArtifactScope.User]: 'General',
}

export const isArtifactScope = (value: unknown): value is ArtifactScope =>
    typeof value === 'string' && (ARTIFACT_SCOPES as string[]).includes(value)
